///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { GtthReportsService } from '../../../../services/project/reports.service';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { finalize, Subscription } from 'rxjs';
import { GtthReportsTabs, REPORT_TABS } from '../reports-tabs.enum';
import { ProjectProductsService } from '../../../logical-topology/engine/project-products-service';
import { ExtendedSectorData } from './extended-sector-data.model';
import { ExtendedLinkData } from '../p2p-info/extended-link-data.model';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { scrollToEl } from '../../../../shared/utils';
import { GtthExportService } from '../../../../services/project/export.service';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { ActivatedRoute } from '@angular/router';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { SectorFeature } from '../../../logical-topology/entities/sector/sector-feature';
import { ProductFrequencies } from '../../../../services/product/product-enums/product-frequencies';
import { MatDialogRef } from '@angular/material/dialog';
import { LoaderIds, PreloaderService } from '../../../../services/preloader.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-report-p2mp-info',
    templateUrl: './report-p2mp-info.component.html',
    styleUrls: ['../../project-windows.scss', './report-p2mp-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthReportP2MPInfoComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    public reportSectorsMap: Map<number, ExtendedSectorData[]>;
    public reportDataToShow: ExtendedSectorData[][];
    public currentTab = GtthReportsTabs.ALL;
    public readonly tabsToShow = REPORT_TABS;
    public selectedSector: ExtendedSectorData;
    public layers: Record<string, string>;

    public readonly loaderKey = LoaderIds.REPORT_P2MP_INFO_ID;
    private layersChangedSubscriber: Subscription;

    constructor(
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<GtthReportP2MPInfoComponent>,
        public historyService: GtthHistoryService,
        private readonly elementRef: ElementRef,
        private readonly reportsService: GtthReportsService,
        private readonly projectProductsService: ProjectProductsService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly exportService: GtthExportService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);

        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            if (this.reportSectorsMap.size > 0) {
                this.filterReport(value);
            }
            this.getProjectLayers();
            this.changeDetector.markForCheck();
        });
    }

    ngOnInit() {
        this.reportSectorsMap = new Map<number, ExtendedSectorData[]>();

        this.getProjectLayers();
        this.preloaderService.show(this.loaderKey);
        this.reportsService
            .generateP2MPInfoReport()
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res: ExtendedSectorData[]) => {
                res.forEach((sector: ExtendedSectorData) => {
                    if (!this.reportSectorsMap.has(sector.sectIdx)) {
                        this.reportSectorsMap.set(sector.sectIdx, []);
                    }
                    this.reportSectorsMap.get(sector.sectIdx).push(sector);

                    if (sector?.sectorLinkData[0]?.usedProd) {
                        const deviceConfig = this.projectProductsService.getBaseRadioById(sector.sectorLinkData[0].usedProd);
                        sector.showInDeviceSectorIndex = deviceConfig?.pdbConfig?.sectorConfig?.length
                            ? deviceConfig.pdbConfig.sectorConfig.length > 0
                            : false;
                    }
                });

                this.getLinksFrozenStatus(this.reportSectorsMap);
                this.filterReport(this.projectInfoService.activeLayer);
                for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
                    const fromNode = link.getFromStation().getName();
                    const toNode = link.getToStation().getName();
                    this.linkSelected(fromNode, toNode, true);
                }
            });
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    getFrequencyChannelValue(frequency): string {
        return ProductFrequencies.getFrequencyChannelName(frequency);
    }

    getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    linkSelected(fromName: string, toName: string, selected: boolean) {
        this.reportDataToShow.forEach((sectors: ExtendedSectorData[]) => {
            sectors.forEach((sector: ExtendedSectorData) => {
                const linkIndex = sector.sectorLinkData.findIndex((link) => {
                    return link.node0Id === fromName && link.node1Id === toName;
                });
                if (linkIndex !== -1) {
                    sector.sectorLinkData[linkIndex].selected = selected;
                    this.scrollToSector(this.generateAggregatedSectorIndex(sector));
                }
            });
        });

        this.changeDetector.markForCheck();
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, isForced);
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, undefined, isInstalled);
    }

    sectorSelected(aggregatedSectorIndex: number, selected: boolean) {
        const sectorIndexes = SectorFeature.getSectorIndexesFromAggregatedIndex(aggregatedSectorIndex);
        if (!selected) {
            this.selectedSector = undefined;
        } else if (
            this.selectedSector &&
            this.selectedSector.sectIdx === sectorIndexes[0] &&
            this.selectedSector.sectIndev === sectorIndexes[1]
        ) {
            return;
        } else {
            this.selectedSector = this.reportDataToShow[sectorIndexes[0]].find((sectorData: ExtendedSectorData) => {
                return sectorData.sectIndev === sectorIndexes[1];
            });
        }
        if (!this.selectedSector) {
            return;
        }

        this.scrollToSector(aggregatedSectorIndex);

        this.changeDetector.markForCheck();
    }

    sectorDirection(uniqueSectorId: number, angle: number) {
        this.sectorSelected(uniqueSectorId, true);
        if (!this.selectedSector) {
            return;
        }
        this.selectedSector.sectDir = angle;
        this.changeDetector.markForCheck();
    }

    closeModal() {
        this.reportSectorsMap = undefined;
        this.dialogRef.close('cancel');
    }

    exportToExcel() {
        this.exportService.exportP2mpReport(this.projectInfoService.activeLayer);
    }

    trackSector(_index, item: ExtendedSectorData) {
        return item.sectIdx;
    }

    trackLinkData(_index, item: ExtendedLinkData) {
        return item.desc;
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.filterReport(this.projectInfoService.activeLayer);
    }

    generateAggregatedSectorIndex(sector): number {
        return sector.sectIdx * 10 + sector.sectIndev;
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    private filterReport(layerIndex = -1) {
        this.reportDataToShow = [];

        this.reportSectorsMap.forEach((sectors: ExtendedSectorData[]) => {
            sectors.forEach((sector: ExtendedSectorData) => {
                const newLinkData = sector.sectorLinkData.filter((link) => {
                    let isLinkToShow = false;

                    switch (this.currentTab) {
                        case GtthReportsTabs.ALL:
                            isLinkToShow = layerIndex === -1 || link.layerIndex === layerIndex;
                            break;
                        case GtthReportsTabs.NEW:
                            isLinkToShow = !link.isUserForced && (layerIndex === -1 || link.layerIndex === layerIndex);
                            break;
                        case GtthReportsTabs.INSTALLED:
                            isLinkToShow = link.isInstalled && (layerIndex === -1 || link.layerIndex === layerIndex);
                            break;
                    }

                    return isLinkToShow;
                });

                if (newLinkData && newLinkData.length > 0) {
                    const newSector = { ...sector };
                    newSector.sectorLinkData = [...newLinkData];
                    if (!this.reportDataToShow[sector.sectIdx]) {
                        this.reportDataToShow[sector.sectIdx] = [];
                    }
                    this.reportDataToShow[sector.sectIdx].push(newSector);
                }
            });
        });

        this.changeDetector.markForCheck();
    }

    private getLinksFrozenStatus(sectors: Map<number, ExtendedSectorData[]>) {
        sectors.forEach((sector) => {
            sector.forEach((sectorData: ExtendedSectorData) => {
                sectorData.sectorLinkData.forEach((linkData) => {
                    const nodeNames = linkData.desc.split(' - ');
                    const link = this.historyService.logicalTopology.getLinkByNodeNames(nodeNames[0], nodeNames[1]);
                    if (!link) {
                        return;
                    }
                    linkData.isUserForced = link.isUserForced();
                    linkData.isInstalled = link.isInstalled();
                    linkData.usedProductName = this.projectProductsService.getBaseRadioNameById(linkData.usedProd);
                });
            });
        });
    }

    private changeLinkFrozenStatus(fromName: string, toName: string, isForced: boolean, isInstalled?: boolean) {
        this.reportDataToShow.forEach((sectors: ExtendedSectorData[]) => {
            const sector = sectors.find((sectorData: ExtendedSectorData) => {
                if (sectorData.nodeid === fromName || sectorData.nodeid === toName) {
                    const link = sectorData.sectorLinkData.find((linkData: ExtendedLinkData) => {
                        return (
                            (linkData.node0Id === fromName || linkData.node1Id === fromName) &&
                            (linkData.node0Id === toName || linkData.node1Id === toName)
                        );
                    });

                    if (link) {
                        link.isUserForced = isDefined(isForced) ? isForced : link.isUserForced;
                        link.isInstalled = isDefined(isInstalled) ? isInstalled : link.isInstalled;
                        this.changeDetector.markForCheck();
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            });

            if (sector) {
                const sectorId = this.generateAggregatedSectorIndex(sector);
                this.scrollToSector(sectorId);
                return;
            }
        });
    }

    private scrollToSector(uniqueSectorId: number) {
        if (this.scrollContainer) {
            scrollToEl(this.scrollContainer.nativeElement, `.sector--${uniqueSectorId}`);
        }
    }
}

@Component({
    selector: 'winde-report-p2mp-info-popup',
    template: '',
    standalone: false
})
export class GtthReportP2MPInfoPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportP2MPInfoComponent,
            ProjectInteractiveWindowsPopupService.REP_P2MP_INFO,
            routerPath,
            bboxParams
        );
    }
}
